/**
 * Data hooks of Compare with SRD and the Customize dialog (1.AC-2 stage s3). Reads: `links.list`
 * (the `variant_of` link out of the document), `reference.get`, `sheets.get`, `types.list` and
 * `search.picker`. Writes: `variants.create` only (never a body, never a reference row).
 */
import {
  type DocHitV1,
  linksList,
  type ReferenceEntryV1,
  referenceGet,
  searchPicker,
  sheetsGet,
  variantsCreate,
} from '@codex/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../../auth/client.js';
import type { CustomizeFields } from '../model/customize.js';
import { variantRequest } from '../model/customize.js';
import { failureOf } from './hooks.js';

export const referenceKey = (docId: string) => ['reference', docId] as const;

/** The SRD entry the document is a variant of: the target of its outgoing `variant_of` link. */
export function useVariantSource(docId: string, enabled: boolean) {
  const query = useQuery({
    queryKey: ['variant-source', docId],
    queryFn: async () => {
      const list = await api(linksList, {
        params: { id: docId },
        query: { direction: 'out', linkTypeKey: 'variant_of' },
      });
      return list.items[0]?.targetId ?? null;
    },
    enabled,
    retry: false,
  });
  return { sourceId: query.data ?? null, loading: enabled && query.isPending };
}

/** A reference entry; a 404 is `missing`. */
export function useReferenceEntry(docId: string | null) {
  const query = useQuery({
    queryKey: referenceKey(docId ?? ''),
    queryFn: (): Promise<ReferenceEntryV1> =>
      api(referenceGet, { params: { id: docId as string } }),
    enabled: docId !== null,
    retry: false,
  });
  return {
    entry: query.data,
    loading: docId !== null && query.isPending,
    failure: query.isError ? failureOf(query.error) : null,
    retry: () => void query.refetch(),
  };
}

/** The document a sheet belongs to (the Customize dialog opened from a sheet id). */
export function useSheetOwner(sheetId: string | null) {
  const query = useQuery({
    queryKey: ['sheet', sheetId ?? ''],
    queryFn: () => api(sheetsGet, { params: { sheetId: sheetId as string } }),
    enabled: sheetId !== null,
    retry: false,
  });
  return {
    documentId: query.data?.documentId ?? null,
    failed: query.isError,
    retry: () => void query.refetch(),
  };
}

/** Canon pages matching `q`, for the Home field (never SRD entries). */
export function useHomeHits(q: string, enabled: boolean) {
  return useQuery({
    queryKey: ['variant-home', q],
    queryFn: async (): Promise<readonly DocHitV1[]> =>
      (await api(searchPicker, { query: { q, limit: 8, includeReference: false } })).canon,
    enabled,
    retry: false,
    gcTime: 0,
  });
}

export function useCreateVariant(sourceDocId: string | null) {
  return useMutation({
    mutationFn: (fields: CustomizeFields) =>
      api(variantsCreate, {
        params: { id: sourceDocId as string },
        body: variantRequest(fields),
      }),
  });
}
